import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import {
  BookOpen,
  Clock,
  CheckCircle,
  ArrowRight,
  Calendar,
  Award,
  Loader,
  AlertCircle,
} from "lucide-react";

interface Course {
  id: string;
  testResultId: string;
  courseData: {
    courseTitle: string;
    courseDescription: string;
    estimatedDuration: string;
    chapters: any[];
  };
  completed: boolean;
  courseTestPassed: boolean;
  createdAt: Timestamp;
  basedOnMistakes?: string[];
}

const MyCourses: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState<Course[]>([]);

  useEffect(() => {
    const fetchCourses = async () => {
      if (!user) {
        setLoading(false);
        return;
      }


      try {
        // Try query with index first
        let coursesQuery = query(
          collection(db, "personalizedCourses"),
          where("userId", "==", user.id),
          orderBy("createdAt", "desc")
        );

        let coursesSnapshot;

        try {
          coursesSnapshot = await getDocs(coursesQuery);
        } catch (indexError: any) {
          // Fallback: query without orderBy (doesn't need index)
          coursesQuery = query(
            collection(db, "personalizedCourses"),
            where("userId", "==", user.id)
          );
          coursesSnapshot = await getDocs(coursesQuery);
        }


        if (coursesSnapshot.size > 0) {
          // Courses found
        }

        const coursesData = coursesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Course[];

        setCourses(coursesData);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [user, db]);

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center space-x-3">
            <BookOpen className="w-8 h-8 text-red-600" />
            <span>My Personalized Courses</span>
          </h1>
          <p className="text-gray-600">
            All courses generated based on your skills test results
          </p>
        </div>

        {/* Courses Grid */}
        {courses.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No Courses Yet
            </h2>
            <p className="text-gray-600 mb-6">
              Take a skills validation test to get your first personalized course!
            </p>
            <button
              onClick={() => navigate("/job-seeker/skills-validation")}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              Take Skills Test
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden"
              >
                {/* Course Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">
                    {course.courseData.courseTitle}
                  </h3>
                  <p className="text-blue-100 text-sm">
                    {course.courseData.chapters.length} Chapters
                  </p>
                </div>

                {/* Course Body */}
                <div className="p-6">
                  <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                    {course.courseData.courseDescription}
                  </p>

                  {/* Course Stats */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span>{course.courseData.estimatedDuration}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <span>Created {formatDate(course.createdAt)}</span>
                    </div>
                  </div>

                  {/* Weak Areas Tags */}
                  {course.basedOnMistakes && course.basedOnMistakes.length > 0 && (
                    <div className="mb-6">
                      <p className="text-xs font-semibold text-gray-600 mb-2">
                        Focuses on:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {course.basedOnMistakes.slice(0, 3).map((area, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="mb-4">
                    {course.courseTestPassed ? (
                      <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                        <Award className="w-5 h-5" />
                        <span className="text-sm font-semibold">
                          Completed & Passed
                        </span>
                      </div>
                    ) : course.completed ? (
                      <div className="flex items-center space-x-2 text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm font-semibold">
                          Completed - Take Test
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-orange-600 bg-orange-50 px-3 py-2 rounded-lg">
                        <Clock className="w-5 h-5" />
                        <span className="text-sm font-semibold">In Progress</span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() =>
                      navigate(`/job-seeker/personalized-course/${course.id}`)
                    }
                    className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors font-semibold"
                  >
                    <span>
                      {course.courseTestPassed
                        ? "Review Course"
                        : course.completed
                        ? "Take Course Test"
                        : "Continue Learning"}
                    </span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
